import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * ScrollHint — subtle animated "scroll down" indicator anchored to the bottom
 * of the viewport. Fades out gracefully once the user starts to scroll and
 * only shows when the page actually has content beneath the fold.
 */
export default function ScrollHint({ label = 'scroll' }) {
    const [visible, setVisible] = useState(false);
    const dismissedRef = useRef(false);

    useEffect(() => {
        // Throttle the check to protect mobile scroll performance
        let raf = 0;
        function check() {
            if (dismissedRef.current) return setVisible(false);
            const y = window.scrollY || document.documentElement.scrollTop || 0;
            const canScroll =
                document.documentElement.scrollHeight - window.innerHeight > 40;
            setVisible(canScroll && y < 40);
        }
        function onScroll() {
            if (raf) return;
            raf = window.requestAnimationFrame(() => {
                raf = 0;
                check();
            });
        }
        function onWheel() {
            dismissedRef.current = true;
            setVisible(false);
        }

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('wheel', onWheel, { passive: true, once: true });
        window.addEventListener('resize', check, { passive: true });

        // Re-check periodically for the first few seconds while lazy content lands.
        const t1 = setTimeout(check, 250);
        const t2 = setTimeout(check, 900);
        const t3 = setTimeout(check, 2000);

        check();
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', check);
            clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
        };
    }, []);

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.5 }}
                    className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] flex flex-col items-center gap-1 pointer-events-none select-none"
                    style={{ willChange: 'opacity, transform' }}
                    data-testid="scroll-hint"
                >
                    <div className="mono text-[9px] tracking-[0.3em] text-[color:var(--parchment)]/70 uppercase">
                        {label}
                    </div>
                    <div className="relative h-8 w-5 rounded-full border border-[color:var(--parchment)]/50" aria-hidden>
                        <motion.div
                            className="absolute left-1/2 -translate-x-1/2 top-1 h-1.5 w-1 rounded-full bg-[color:var(--lavender)]"
                            animate={{ y: [0, 10, 0], opacity: [1, 0.2, 1] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                            style={{ willChange: 'transform, opacity' }}
                        />
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
